import { QueryObserverResult, UseQueryOptions, useQuery } from '@tanstack/react-query'

import { getBinaryFileContentBySignedUrl } from 'services'
import { QueryKeys } from 'services/queryKeys'
import { getBinaryFileContent, getFileContent, getVerificationAttachmentSignedUrl } from 'services/results'
import type {
  GetBinaryFileContentArgs,
  GetBinaryFileContentResponse,
  GetFileContentResponse,
  GetTextFileContentArgs,
  GetVerificationAttachmentSignedUrlParams,
  GetVerificationAttachmentSignedUrlResponse
} from 'services/results/types'

export const useTextFileContent = (
  args: GetTextFileContentArgs,
  options?: Omit<UseQueryOptions<GetFileContentResponse | undefined, { fetchSignedUrlOnly: boolean }>, 'queryKey'>
): QueryObserverResult<GetFileContentResponse | undefined, { fetchSignedUrlOnly: boolean }> => {
  return useQuery<GetFileContentResponse | undefined, { fetchSignedUrlOnly: boolean }>({
    queryKey: QueryKeys.GetTextFileContent(args),
    queryFn: () => getFileContent(args),
    ...options
  })
}

export const useBinaryFileContent = (
  args: GetBinaryFileContentArgs,
  options?: Omit<UseQueryOptions<GetBinaryFileContentResponse | undefined, { fetchSignedUrlOnly: boolean }>, 'queryKey'>
): QueryObserverResult<GetBinaryFileContentResponse | undefined, { fetchSignedUrlOnly: boolean }> => {
  return useQuery<GetBinaryFileContentResponse | undefined, { fetchSignedUrlOnly: boolean }>({
    queryKey: QueryKeys.GetBinaryFileContent(args),
    queryFn: () => getBinaryFileContent(args),
    ...options
  })
}

export const useBinaryFileContentFromSignedUrl = (
  signedUrl: string,
  options?: Omit<UseQueryOptions<GetBinaryFileContentResponse | undefined>, 'queryKey'>
): QueryObserverResult<GetBinaryFileContentResponse | undefined> => {
  return useQuery<GetBinaryFileContentResponse | undefined>({
    queryKey: QueryKeys.GetBinaryFileContentFromSignedUrl(signedUrl),
    queryFn: async () => {
      const fileContent = await getBinaryFileContentBySignedUrl(signedUrl)
      return { signedUrl, fileKey: signedUrl, fileContent }
    },
    ...options
  })
}

export const useVerificationAttachmentSignedUrl = (
  params: GetVerificationAttachmentSignedUrlParams,
  options?: Omit<UseQueryOptions<GetVerificationAttachmentSignedUrlResponse>, 'queryKey'>
): QueryObserverResult<GetVerificationAttachmentSignedUrlResponse> => {
  return useQuery<GetVerificationAttachmentSignedUrlResponse>({
    queryKey: QueryKeys.GetVerificationAttachmentSignedUrl(params),
    queryFn: () => getVerificationAttachmentSignedUrl(params),
    ...options
  })
}
